import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Trash2, Clock, MapPin, User, Ticket, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { buildApiUrl, API_CONFIG } from '../config/api';

interface TicketValidation {
  id: number;
  ticket_id: number;
  ticket_unique_string: string;
  passenger_name: string | null;
  station_id: number;
  station_name: string;
  line_id: number | null;
  validation_type: 'entry' | 'exit';
  validation_status: 'approved' | 'rejected';
  rejection_reason: string | null;
  scanned_at: string;
  ticket_status: string;
}

const TicketValidationManager: React.FC = () => {
  const [validations, setValidations] = useState<TicketValidation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'entry' | 'exit'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'approved' | 'rejected'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);

  // Fetch validations when page, search, or filters change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchValidations();
    }, searchQuery ? 1000 : 0); // Debounce only for search, immediate for page/filter change

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchQuery, filterType, filterStatus]);

  // Reset to page 1 when search or filters change
  useEffect(() => {
    if ((searchQuery || filterType !== 'all' || filterStatus !== 'all') && currentPage !== 1) {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filterType, filterStatus]);

  const fetchValidations = async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * itemsPerPage;
      const params = new URLSearchParams({
        skip: skip.toString(),
        limit: itemsPerPage.toString(),
      });

      if (searchQuery) params.append('search', searchQuery);
      if (filterType !== 'all') params.append('validation_type', filterType);
      if (filterStatus !== 'all') params.append('validation_status', filterStatus);

      const response = await fetch(buildApiUrl(`${API_CONFIG.ENDPOINTS.VALIDATIONS}?${params}`));
      if (!response.ok) throw new Error('Failed to fetch validations');
      const data = await response.json();
      setValidations(data.data || []);
      setTotalItems(data.total || 0);
      setError('');
    } catch (err) {
      setError('Failed to load validation records');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const deleteValidation = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this validation record?')) return;

    try {
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.VALIDATION_BY_ID(id)), {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete validation');

      await fetchValidations();
      alert('Validation deleted successfully');
    } catch (err) {
      alert('Failed to delete validation');
      console.error(err);
    }
  };

  const updateValidationStatus = async (id: number, newStatus: 'approved' | 'rejected') => {
    try {
      const response = await fetch(
        buildApiUrl(API_CONFIG.ENDPOINTS.VALIDATION_UPDATE_STATUS(id, newStatus)),
        { method: 'POST' }
      );
      if (!response.ok) throw new Error('Failed to update validation');

      await fetchValidations();
      alert('Validation status updated successfully');
    } catch (err) {
      alert('Failed to update validation status');
      console.error(err);
    }
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading validations...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Ticket Validation Management</h1>
        <p className="text-gray-600">View and manage ticket check-in/check-out records</p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Search and Filters */}
      <div className="mb-6 flex gap-4 flex-wrap">
        <div className="flex-1 min-w-[300px]">
          <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by ticket ID, passenger, or station..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Types</option>
            <option value="entry">Entry Only</option>
            <option value="exit">Exit Only</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Status</option>
            <option value="approved">Approved Only</option>
            <option value="rejected">Rejected Only</option>
          </select>
        </div>

        <div className="flex items-end">
          <button
            onClick={fetchValidations}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-600">Total Validations</div>
          <div className="text-2xl font-bold text-gray-800">{totalItems}</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="text-sm text-green-600">Approved</div>
          <div className="text-2xl font-bold text-green-700">
            {validations.filter(v => v.validation_status === 'approved').length}
          </div>
        </div>
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <div className="text-sm text-red-600">Rejected</div>
          <div className="text-2xl font-bold text-red-700">
            {validations.filter(v => v.validation_status === 'rejected').length}
          </div>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="text-sm text-blue-600">Current Page</div>
          <div className="text-2xl font-bold text-blue-700">{validations.length}</div>
        </div>
      </div>

      {/* Validations Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ticket</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Passenger</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Station</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scanned At</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {validations.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    No validation records found
                  </td>
                </tr>
              ) : (
                validations.map((validation) => (
                  <tr key={validation.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{validation.id}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Ticket className="w-4 h-4 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {validation.ticket_unique_string}
                          </div>
                          <div className="text-xs text-gray-500">
                            {validation.ticket_status}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900">
                          {validation.passenger_name || 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900">{validation.station_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          validation.validation_type === 'entry'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-purple-100 text-purple-700'
                        }`}
                      >
                        {validation.validation_type === 'entry' ? '→ Entry' : '← Exit'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {validation.validation_status === 'approved' ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500" />
                        )}
                        <span
                          className={`text-sm font-medium ${
                            validation.validation_status === 'approved'
                              ? 'text-green-700'
                              : 'text-red-700'
                          }`}
                        >
                          {validation.validation_status}
                        </span>
                      </div>
                      {validation.rejection_reason && (
                        <div className="text-xs text-gray-500 mt-1">
                          {validation.rejection_reason}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900">
                          {formatDateTime(validation.scanned_at)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {validation.validation_status === 'rejected' && (
                          <button
                            onClick={() => updateValidationStatus(validation.id, 'approved')}
                            className="p-1 text-green-600 hover:bg-green-50 rounded"
                            title="Approve"
                          >
                            <CheckCircle className="w-5 h-5" />
                          </button>
                        )}
                        {validation.validation_status === 'approved' && (
                          <button
                            onClick={() => updateValidationStatus(validation.id, 'rejected')}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                            title="Reject"
                          >
                            <XCircle className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => deleteValidation(validation.id)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="text-sm text-gray-600">
            Showing {validations.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to{' '}
            {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} results
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className={`flex items-center gap-1 px-3 py-2 border rounded-lg transition-colors ${
                currentPage === 1
                  ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-100'
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
            <div className="text-sm text-gray-700 px-4">
              Page {currentPage} of {totalPages}
            </div>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className={`flex items-center gap-1 px-3 py-2 border rounded-lg transition-colors ${
                currentPage === totalPages
                  ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-100'
              }`}
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketValidationManager;
